/**
 * MyRegistrationsPage
 *
 * Personal-view page for staff to see THEIR own walk-ins + leads. Solves
 * two daily pain points the staff reported:
 *
 *   1. "Did I already register this customer this morning?" → quick
 *      glance at their own list answers it in 2 sec without bothering
 *      the admin.
 *
 *   2. "I called, customer said follow up in 5 days" → tap "Reschedule"
 *      on the row, pick the new date, optionally append a note. The
 *      reminder re-fires automatically on the new date (the backend
 *      resets `follow_up_reminder_sent`).
 *
 * Access: any logged-in user. Admin / manager still see everyone's via
 * /admin → Walk-ins + Leads tabs.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { api, todayIst } from '../lib/store';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import {
  CalendarClock, RefreshCw, Search, UserCheck, FlagTriangleRight,
  Loader2, ChevronDown, ChevronRight, Save, Phone, CheckCircle2, Pencil,
} from 'lucide-react';
import WalkinFormDialog from '../components/forms/WalkinFormDialog';
import { OrderStatusButtons } from '../components/cart/OrderStatusButtons';

// Shared filter-chip row — same look/feel as the Staff tab
// (● All | ● Closed | ● Pending | ● Lost with counts). Missing / null
// / empty order_status buckets as Pending, matching the backend rule.
const STATUS_CHIPS = [
  { key: 'all',     label: 'All',     dot: 'bg-[#8F9485]',  active: 'bg-[#8F9485]/10 border-[#8F9485]/40 text-[#5C6150]' },
  { key: 'closed',  label: 'Closed',  dot: 'bg-[#2E7D32]',  active: 'bg-[#2E7D32]/10 border-[#2E7D32]/40 text-[#1B5E20]' },
  { key: 'pending', label: 'Pending', dot: 'bg-[#F9A825]',  active: 'bg-[#F9A825]/10 border-[#F9A825]/40 text-[#8B6914]' },
  { key: 'lost',    label: 'Lost',    dot: 'bg-[#C62828]',  active: 'bg-[#C62828]/10 border-[#C62828]/40 text-[#8B2E2E]' },
];
const normStatus = (x) => {
  const s = (x?.order_status || '').toLowerCase();
  return (s === 'closed' || s === 'lost') ? s : 'pending';
};
function StatusFilterChips({ rows, value, onChange, testidPrefix }) {
  const counts = { all: rows.length, closed: 0, pending: 0, lost: 0 };
  rows.forEach(r => { counts[normStatus(r)] += 1; });
  return (
    <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidPrefix}-status-filter`}>
      {STATUS_CHIPS.map(c => {
        const isActive = value === c.key;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onChange(c.key)}
            className={
              'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors ' +
              (isActive ? c.active : 'bg-white border-[#E2E6D9] text-[#5C6150] hover:border-[#8F9485]')
            }
            data-testid={`${testidPrefix}-status-chip-${c.key}`}
          >
            <span className={`w-2 h-2 rounded-full ${c.dot}`} />
            {c.label} <span className="text-[10px] text-[#8F9485]">{counts[c.key]}</span>
          </button>
        );
      })}
    </div>
  );
}

// IST calendar date — matches the follow-up reminder scheduler so
// "overdue" here means the same thing as it does on the WhatsApp digest.
// iter-119b: switched to the shared `todayIst` helper (see lib/store.js)
// which is device-timezone-independent — the old local copy used
// `getTimezoneOffset` and double-counted on IST phones after 18:30.
const todayIstIso = todayIst;

function fmtIstDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtIstDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// LeadStateBadge REMOVED. The Closed / Pending / Lost
// segmented control now shown on every lead row (via OrderStatusButtons)
// is the single source of truth for a lead's outcome; a separate
// ACTIVE/DONE badge next to it was redundant and could visually
// contradict the control on legacy leads that still have `done=true`
// but no `order_status` set.

function RescheduleDialog({ open, onClose, item, kind, onSaved }) {
  // `kind` is 'walkin' or 'lead'. Reused for both so we have one source
  // of truth for the reschedule UX.
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      // Iter 116: do NOT pre-fill a default date. Staff asked for a
      // blank field so they can't accidentally accept the prefilled
      // "+5 days" without thinking. We offer quick-pick chips below.
      setDate('');
      setTime(item.follow_up_time || '');
      setNote('');
    }
  }, [open, item]);

  // Quick-pick chips: most common reschedule offsets the staff use on a
  // call. Tapping a chip fills the date input — no math required.
  const today = new Date();
  const isoOffset = (days) => {
    const d = new Date(today);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const quickChips = [
    { label: 'Tomorrow',  days: 1 },
    { label: 'In 3 days', days: 3 },
    { label: 'In 5 days', days: 5 },
    { label: '1 week',    days: 7 },
    { label: '15 days',   days: 15 },
    { label: '1 month',   days: 30 },
  ];

  const handleSave = async () => {
    if (!date) {
      toast.error('Pick a follow-up date');
      return;
    }
    setSaving(true);
    try {
      if (kind === 'walkin') {
        await api.rescheduleWalkin(item.id, { follow_up_date: date, follow_up_time: time, notes: note });
      } else {
        await api.rescheduleLead(item.id, { follow_up_date: date, notes: note });
      }
      toast.success('Follow-up rescheduled');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      logger.error('Reschedule failed:', err);
      toast.error(err.response?.data?.detail || 'Reschedule failed');
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule follow-up</DialogTitle>
          <DialogDescription>
            Set the new date when you should call <strong>{item.customer_name}</strong> again.
            A WhatsApp reminder will fire on that date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">New follow-up date *</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1"
              data-testid="reschedule-date"
            />
            {/* Quick-pick chips — tap to autofill the date without
                opening the native date picker. Staff can still override
                by typing or picking from the calendar. */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {quickChips.map((c) => (
                <Button
                  key={c.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDate(isoOffset(c.days))}
                  className="h-7 text-[11px] px-2 border-[#556B2F]/40 text-[#556B2F] hover:bg-[#556B2F]/5"
                  data-testid={`reschedule-chip-${c.days}d`}
                >
                  {c.label}
                </Button>
              ))}
            </div>
          </div>
          {kind === 'walkin' && (
            <div>
              <Label className="text-xs">Time (optional, 24h HH:MM)</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="mt-1"
                data-testid="reschedule-time"
              />
              <p className="text-[10px] text-[#8F9485] mt-1">Leave blank for default 09:30 IST reminder.</p>
            </div>
          )}
          <div>
            <Label className="text-xs">Call note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What did the customer say? (gets appended to notes with a timestamp)"
              rows={3}
              className="mt-1 text-sm"
              data-testid="reschedule-note"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="reschedule-cancel">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#556B2F] hover:bg-[#465926] text-white" data-testid="reschedule-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Reschedule
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Shared confirm dialog used for the "Complete" action on
// walk-ins, leads and quotations. Kept dependency-light (no shadcn
// AlertDialog import churn) — a small Dialog with two buttons.
function CompleteConfirmDialog({ open, onCancel, onConfirm, saving, kindLabel, itemName }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className="sm:max-w-md" data-testid="complete-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Mark this follow-up as complete?</DialogTitle>
          <DialogDescription>
            No further reminders will be sent for this {kindLabel.toLowerCase()}.
            {itemName ? <> Customer: <strong>{itemName}</strong>.</> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="complete-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={saving}
            className="bg-[#556B2F] hover:bg-[#465926] text-white"
            data-testid="complete-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Complete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WalkinRow({ w, onReschedule, onEdit, onStatusChange, onStatusChanged }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="p-3 border border-[#E2E6D9]" data-testid={`my-walkin-${w.id}`}>
      {/* Collapsed row: just badge + name (+ existing 'Reminder sent'
          hint if present). Everything else lives inside the expand. */}
      <button type="button" className="flex items-center gap-2 text-left w-full" onClick={() => setExpanded(x => !x)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-[#5b6450]" /> : <ChevronRight className="w-4 h-4 text-[#5b6450]" />}
        <Badge variant="outline" className="text-[9px] py-0 px-1 uppercase border-amber-500 text-amber-700 shrink-0">Walk-in</Badge>
        <span className="font-semibold text-sm text-[#1f2a16] truncate flex-1 min-w-0">{w.customer_name}</span>
        {w.follow_up_reminder_sent && (
          <Badge variant="outline" className="text-[10px] py-0 px-1 border-emerald-500 text-emerald-700 shrink-0">Reminder sent</Badge>
        )}
      </button>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-dashed border-[#1f2a16]/10 text-[11px] text-[#5b6450] space-y-1">
          <p><Phone className="w-3 h-3 inline mr-1" />{w.customer_phone || 'no phone'}</p>
          {w.products_shown && <p><strong>Products:</strong> {w.products_shown}</p>}
          {w.location && <p data-testid={`my-walkin-location-${w.id}`}>📍 {w.location}</p>}
          <p className="flex items-center gap-1">
            <CalendarClock className="w-3 h-3" />
            Follow-up: <strong>{fmtIstDate(w.follow_up_date)}</strong>{w.follow_up_time ? ` at ${w.follow_up_time}` : ''}
          </p>
          {w.notes && <p><strong>Notes:</strong> <span className="whitespace-pre-wrap">{w.notes}</span></p>}
          {w.architect_name && <p><strong>Architect:</strong> {w.architect_name}</p>}
          {w.carpenter_name && <p><strong>Carpenter:</strong> {w.carpenter_name}</p>}
          {w.rate_quoted && <p><strong>Rate quoted:</strong> {w.rate_quoted}</p>}
          {(w.reschedule_history || []).length > 0 && (
            <details>
              <summary className="cursor-pointer text-[#556B2F]">Reschedule history ({w.reschedule_history.length})</summary>
              <ul className="ml-3 mt-1 list-disc text-[10px]">
                {w.reschedule_history.map((r, i) => (
                  <li key={`${r.rescheduled_at}-${i}`}>{fmtIstDateTime(r.rescheduled_at)}: {r.previous_date || 'unset'} → {r.new_date} {r.note ? `· "${r.note}"` : ''}</li>
                ))}
              </ul>
            </details>
          )}
          <p className="text-[10px] text-[#8F9485]">Created {fmtIstDateTime(w.created_at)}</p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            <Button size="sm" variant="outline" onClick={() => onReschedule(w)} className="h-7 text-xs" data-testid={`my-walkin-resched-${w.id}`}>
              <RefreshCw className="w-3 h-3 mr-1" /> Reschedule
            </Button>
            <Button size="sm" variant="outline" onClick={() => onEdit(w)} className="h-7 text-xs border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F]/5" data-testid={`my-walkin-edit-${w.id}`}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
          </div>
          {/* Order-status control on every row. Same widget
              as the quotation cards; owner-only enforcement lives on
              the server. */}
          <div className="mt-2">
            <OrderStatusButtons
              recordId={w.id}
              status={w.order_status}
              lostReason={w.lost_reason}
              onStatusChange={onStatusChange}
              onChange={onStatusChanged}
              size="sm"
            />
          </div>
        </div>
      )}
    </Card>
  );
}

function LeadRow({ l, onReschedule, onStatusChange, onStatusChanged }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="p-3 border border-[#E2E6D9]" data-testid={`my-lead-${l.id}`}>
      <button type="button" className="flex items-center gap-2 text-left w-full" onClick={() => setExpanded(x => !x)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-[#5b6450]" /> : <ChevronRight className="w-4 h-4 text-[#5b6450]" />}
        <Badge variant="outline" className="text-[9px] py-0 px-1 uppercase border-sky-500 text-sky-700 shrink-0">Lead</Badge>
        <span className="font-semibold text-sm text-[#1f2a16] truncate flex-1 min-w-0">{l.customer_name}</span>
      </button>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-dashed border-[#1f2a16]/10 text-[11px] text-[#5b6450] space-y-1">
          <p><Phone className="w-3 h-3 inline mr-1" />{l.customer_phone || 'no phone'}</p>
          {l.location && <p data-testid={`my-lead-location-${l.id}`}>📍 {l.location}</p>}
          <p className="flex items-center gap-1">
            <CalendarClock className="w-3 h-3" />
            Follow-up: <strong>{fmtIstDate(l.follow_up_date)}</strong>
            {l.timeline && <span>· Timeline: {l.timeline}</span>}
          </p>
          {l.notes && <p><strong>Notes:</strong> <span className="whitespace-pre-wrap">{l.notes}</span></p>}
          {l.budget_range && <p><strong>Budget:</strong> {l.budget_range}</p>}
          {l.source && <p><strong>Source:</strong> {l.source}</p>}
          {l.interest_area && <p><strong>Interest:</strong> {l.interest_area}</p>}
          {(l.reschedule_history || []).length > 0 && (
            <details>
              <summary className="cursor-pointer text-[#556B2F]">Reschedule history ({l.reschedule_history.length})</summary>
              <ul className="ml-3 mt-1 list-disc text-[10px]">
                {l.reschedule_history.map((r, i) => (
                  <li key={`${r.rescheduled_at}-${i}`}>{fmtIstDateTime(r.rescheduled_at)}: {r.previous_date || 'unset'} → {r.new_date} {r.note ? `· "${r.note}"` : ''}</li>
                ))}
              </ul>
            </details>
          )}
          <p className="text-[10px] text-[#8F9485]">Created {fmtIstDateTime(l.created_at)}</p>
          <Button size="sm" variant="outline" onClick={() => onReschedule(l)} className="h-7 text-xs mt-1" data-testid={`my-lead-resched-${l.id}`}>
            <RefreshCw className="w-3 h-3 mr-1" /> Reschedule
          </Button>
          {/* Order-status control (owner-only via backend). */}
          <div className="mt-2">
            <OrderStatusButtons
              recordId={l.id}
              status={l.order_status}
              lostReason={l.lost_reason}
              onStatusChange={onStatusChange}
              onChange={onStatusChanged}
              size="sm"
            />
          </div>
        </div>
      )}
    </Card>
  );
}

// Collapsed overdue row: badge + name only. Expanding reveals details
// (follow-up date, phone, notes) and the "Reschedule" / "Complete"
// actions. Complete triggers the confirmation dialog via the parent.
function OverdueRow({ it, onReschedule, onComplete, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const kindLabel = it.kind === 'walkin' ? 'Walk-in' : 'Lead';
  const kindClass = it.kind === 'walkin'
    ? 'border-amber-500 text-amber-700'
    : 'border-sky-500 text-sky-700';
  return (
    <div
      className="bg-white rounded border border-red-200"
      data-testid={`myreg-overdue-${it.id}`}
    >
      <button
        type="button"
        className="w-full flex items-center gap-2 px-2 py-1.5 text-left"
        onClick={() => setExpanded((x) => !x)}
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-[#5b6450]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#5b6450]" />}
        <Badge variant="outline" className={`text-[9px] py-0 px-1 uppercase shrink-0 ${kindClass}`}>{kindLabel}</Badge>
        <span className="text-xs font-medium text-[#1f2a16] truncate flex-1 min-w-0">{it.customer_name}</span>
      </button>
      {expanded && (
        <div className="px-2 pb-2 pt-1 border-t border-red-100 text-[11px] text-[#5b6450] space-y-1">
          <p className="text-red-700 font-medium">Follow-up date: {it.follow_up_date}</p>
          <p><Phone className="w-3 h-3 inline mr-1" />{it.customer_phone || '—'}</p>
          {it.notes && <p><strong>Notes:</strong> <span className="whitespace-pre-wrap">{it.notes}</span></p>}
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReschedule(it, it.kind)}
              className="h-7 text-[11px] px-2"
              data-testid={`myreg-overdue-reschedule-${it.id}`}
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Reschedule
            </Button>
            {/* Complete button removed for BOTH walk-ins
                and leads. Ending a follow-up is now unified across
                the two: mark Closed or Lost via the status control
                on the row (or the tab list below), which auto-drops
                the row out of overdue via the pending-status guard. */}
            {/* Walk-in Edit lives ONLY next to walk-in
                overdue rows. Lead editing is a separate feature that
                would need its own dialog (see LeadFormDialog) — out
                of scope for this task per spec. */}
            {it.kind === 'walkin' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(it)}
                className="h-7 text-[11px] px-2 border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F]/5"
                data-testid={`myreg-overdue-edit-${it.id}`}
              >
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Overdue-list row specifically for a quotation follow-up.
// `QuotationOverdueRow` and `QuotationRescheduleDialog`
// components were removed. Quotations have their own dedicated
// tracking flow (Quotations page + WhatsApp digest); My Registrations
// is now strictly walk-ins + leads. Their state (`quoteReschedItem`)
// and API fetch (`getMyQuotationFollowups`) had already been stripped
// from the main component in an earlier iteration; these two orphan
// component functions were the last dangling remnants.

export default function MyRegistrationsPage() {
  const [tab, setTab] = useState('walkins');
  const [search, setSearch] = useState('');
  const [walkins, setWalkins] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reschedItem, setReschedItem] = useState(null);
  const [reschedKind, setReschedKind] = useState('walkin');
  const [completeCtx, setCompleteCtx] = useState(null);
  const [completing, setCompleting] = useState(false);
  // Single edit-target — when set, WalkinFormDialog opens
  // with editRecord prefilled. Any staff can hit this row from the
  // overdue box or the Walk-ins tab.
  const [walkinToEdit, setWalkinToEdit] = useState(null);
  // Per-tab status-chip filter. Persists as long as the
  // page is mounted; resets on navigation away.
  const [walkinStatusFilter, setWalkinStatusFilter] = useState('all');
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, lRes] = await Promise.all([
        api.listMyWalkins({ search, limit: 200 }),
        api.listMyLeads({ search, limit: 200 }),
      ]);
      setWalkins(wRes.data.walkins || []);
      setLeads(lRes.data.leads || []);
    } catch (err) {
      logger.error('Failed to load my registrations:', err);
      toast.error('Failed to load your registrations');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openReschedule = (item, kind) => {
    setReschedKind(kind);
    setReschedItem(item);
  };

  // Open the confirm dialog for any type. Actual call
  // happens after the user confirms via the CompleteConfirmDialog.
  const openComplete = (item, kind) => {
    setCompleteCtx({ item, kind });
  };

  const doComplete = async () => {
    if (!completeCtx) return;
    setCompleting(true);
    try {
      const { item, kind } = completeCtx;
      if (kind === 'walkin') {
        await api.completeWalkin(item.id);
      } else if (kind === 'lead') {
        await api.completeLead(item.id);
      } else if (kind === 'quote') {
        // Existing PATCH null path — no dedicated /complete endpoint
        // needed for quotations per spec (Task 1c).
        await api.setQuotationFollowUp(item.id, null);
      }
      toast.success('Follow-up completed');
      setCompleteCtx(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete follow-up');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      {/* Overdue box — logic MOVED from TodayFollowupsBanner: shows only
          when the user has 1+ overdue follow-ups (walk-in or lead), with
          the same "Done — reschedule" action. Handoff opens the same
          RescheduleDialog used by the tabs below. */}
      {(() => {
        const today = todayIstIso();
        // Order_status guard — a walk-in / lead only counts
        // as overdue if it's still pending. Marking Closed or Lost via
        // the new status control drops it from overdue immediately.
        // Missing / null / '' order_status ≡ pending (matches Task 1).
        const DATE_RX = /^\d{4}-\d{2}-\d{2}$/;
        const isPending = (x) => !x.order_status || String(x.order_status).toLowerCase() === 'pending';
        const isOverdueDate = (d) => typeof d === 'string' && DATE_RX.test(d) && d <= today;
        const overdueWalkins = walkins
          .filter((w) => isOverdueDate(w.follow_up_date) && isPending(w) && (!w.follow_up_reminder_sent || w.status !== 'closed'))
          .map((w) => ({ kind: 'walkin', ...w }));
        const overdueLeads = leads
          .filter((l) => {
            if (!isOverdueDate(l.follow_up_date)) return false;
            // Unify on order_status; legacy done/status
            // guards kept as belt-and-suspenders so old records
            // that were pre-marked done/converted stay excluded.
            if (!isPending(l)) return false;
            if (l.done === true) return false;
            const s = (l.status || '').toLowerCase();
            return !['closed', 'lost', 'converted'].includes(s);
          })
          .map((l) => ({ kind: 'lead', ...l }));
        const overdue = [...overdueWalkins, ...overdueLeads].sort((a, b) =>
          (a.follow_up_date || '').localeCompare(b.follow_up_date || ''));
        const totalOverdue = overdue.length;
        if (totalOverdue === 0) return null;
        return (
          <Card
            className="p-3 border-l-4 border-l-red-500 border border-red-300 bg-red-50/60"
            data-testid="myreg-overdue-box"
          >
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 text-red-700" />
              <span className="text-sm font-semibold text-red-800">
                <span data-testid="myreg-overdue-count">{totalOverdue} overdue</span>
                {' '}follow-up{totalOverdue === 1 ? '' : 's'}
              </span>
            </div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {overdue.map((it) => (
                <OverdueRow
                  key={`${it.kind}-${it.id}`}
                  it={it}
                  onReschedule={openReschedule}
                  onComplete={openComplete}
                  onEdit={setWalkinToEdit}
                />
              ))}
            </div>
          </Card>
        );
      })()}

      <Card className="bg-[#F5F2E8] border-[#1f2a16]/15">
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
          <CardTitle className="flex items-center gap-2 text-[#1f2a16] text-base">
            <UserCheck className="w-4 h-4 text-[#556B2F]" />
            My Registrations
          </CardTitle>
          <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} data-testid="myreg-refresh">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#8F9485]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by customer name / phone / product…"
              className="pl-8 text-sm h-9"
              data-testid="myreg-search"
            />
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-[#E2E6D9]">
              <TabsTrigger value="walkins" data-testid="myreg-tab-walkins">
                <UserCheck className="w-3 h-3 mr-1" /> Walk-ins
                <Badge variant="outline" className="ml-1.5 text-[10px] py-0 px-1 border-[#1f2a16]/30">{walkins.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="leads" data-testid="myreg-tab-leads">
                <FlagTriangleRight className="w-3 h-3 mr-1" /> Leads
                <Badge variant="outline" className="ml-1.5 text-[10px] py-0 px-1 border-[#1f2a16]/30">{leads.length}</Badge>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="walkins" className="space-y-2 mt-3">
              {!loading && walkins.length === 0 && (
                <p className="text-center text-sm text-[#5b6450] py-8">No walk-ins yet. Register one from the homepage.</p>
              )}
              {walkins.length > 0 && (
                <StatusFilterChips
                  rows={walkins}
                  value={walkinStatusFilter}
                  onChange={setWalkinStatusFilter}
                  testidPrefix="myreg-walkins"
                />
              )}
              {walkins
                .filter(w => walkinStatusFilter === 'all' || normStatus(w) === walkinStatusFilter)
                .map((w) => (
                  <WalkinRow
                    key={w.id}
                    w={w}
                    onReschedule={(it) => openReschedule(it, 'walkin')}
                    onEdit={setWalkinToEdit}
                    onStatusChange={(s, r) => api.setWalkinStatus(w.id, { status: s, lost_reason: r })}
                    onStatusChanged={(newStatus, newReason) => {
                      setWalkins(prev => prev.map(x => x.id === w.id
                        ? { ...x, order_status: newStatus, lost_reason: newReason || '' }
                        : x));
                    }}
                  />
                ))}
            </TabsContent>
            <TabsContent value="leads" className="space-y-2 mt-3">
              {!loading && leads.length === 0 && (
                <p className="text-center text-sm text-[#5b6450] py-8">No leads yet. Register one from the homepage.</p>
              )}
              {leads.length > 0 && (
                <StatusFilterChips
                  rows={leads}
                  value={leadStatusFilter}
                  onChange={setLeadStatusFilter}
                  testidPrefix="myreg-leads"
                />
              )}
              {leads
                .filter(l => leadStatusFilter === 'all' || normStatus(l) === leadStatusFilter)
                .map((l) => (
                  <LeadRow
                    key={l.id}
                    l={l}
                    onReschedule={(it) => openReschedule(it, 'lead')}
                    onStatusChange={(s, r) => api.setLeadStatus(l.id, { status: s, lost_reason: r })}
                    onStatusChanged={(newStatus, newReason) => {
                      setLeads(prev => prev.map(x => x.id === l.id
                        ? { ...x, order_status: newStatus, lost_reason: newReason || '' }
                        : x));
                    }}
                  />
                ))}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <RescheduleDialog
        open={!!reschedItem}
        onClose={() => setReschedItem(null)}
        item={reschedItem}
        kind={reschedKind}
        onSaved={fetchData}
      />
      <CompleteConfirmDialog
        open={!!completeCtx}
        onCancel={() => { if (!completing) setCompleteCtx(null); }}
        onConfirm={doComplete}
        saving={completing}
        kindLabel={completeCtx?.kind === 'walkin' ? 'Walk-in' : completeCtx?.kind === 'lead' ? 'Lead' : 'Quotation'}
        itemName={completeCtx?.item?.customer_name}
      />
      <WalkinFormDialog
        open={!!walkinToEdit}
        onOpenChange={(o) => { if (!o) setWalkinToEdit(null); }}
        editRecord={walkinToEdit}
        onUpdated={() => { setWalkinToEdit(null); fetchData(); }}
      />
    </div>
  );
}
